import { X, Download, Music2, Play, Pause } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "./button";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useToast } from "@/features/toast";
import { miniAppBridgeService } from "@/api/services";

interface AudioViewerProps {
  audio: string;
  onClose: () => void;
  messageId?: string;
  title?: string;
}

export function AudioViewer({ audio, onClose, title }: AudioViewerProps) {
  const { t } = useTranslation();
  const { success: successToast, error: errorToast } = useToast();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const progressBarRef = useRef<HTMLDivElement>(null);

  // Audio event handlers
  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) return;

    const updateTime = () => setCurrentTime(audioElement.currentTime);
    const updateDuration = () => setDuration(audioElement.duration);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    audioElement.addEventListener("timeupdate", updateTime);
    audioElement.addEventListener("loadedmetadata", updateDuration);
    audioElement.addEventListener("play", handlePlay);
    audioElement.addEventListener("pause", handlePause);
    audioElement.addEventListener("ended", handleEnded);

    return () => {
      audioElement.removeEventListener("timeupdate", updateTime);
      audioElement.removeEventListener("loadedmetadata", updateDuration);
      audioElement.removeEventListener("play", handlePlay);
      audioElement.removeEventListener("pause", handlePause);
      audioElement.removeEventListener("ended", handleEnded);
    };
  }, []);

  // Format time helper
  const formatTime = (seconds: number) => {
    if (!isFinite(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Handle progress bar click
  const handleProgressChange = (e: React.MouseEvent<HTMLDivElement>) => {
    const audioElement = audioRef.current;
    if (!audioElement || !progressBarRef.current) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;

    audioElement.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Toggle play/pause
  const handleTogglePlay = () => {
    const audioElement = audioRef.current;
    if (!audioElement) return;

    if (isPlaying) {
      audioElement.pause();
    } else {
      audioElement.play();
    }
  };

  // ESC tuşu ile kapatma ve body scroll lock
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    // Lock body scroll when viewer is open
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [onClose]);

  const handleDownload = async () => {
    try {
      // Detect file extension from URL
      const urlLower = audio.toLowerCase();
      let extension = "wav"; // default
      if (urlLower.includes(".mp3")) extension = "mp3";
      else if (urlLower.includes(".wav")) extension = "wav";
      else if (urlLower.includes(".m4a")) extension = "m4a";
      else if (urlLower.includes(".aac")) extension = "aac";
      else if (urlLower.includes(".flac")) extension = "flac";
      else if (urlLower.includes(".oga")) extension = "oga";

      // Fetch the file as blob
      const response = await fetch(audio);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `audio-${Date.now()}.${extension}`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      successToast(t("wizard.downloadStarted") || "İndirme başlatıldı");
    } catch (errorResponse: any) {
      console.error("Download failed:", errorResponse);
      // Fallback: Open in new tab
      window.open(audio, "_blank");
      errorToast(t("wizard.downloadFailed") || "İndirme başarısız");
    }
  };

  const fileName = audio.split("/").pop() || "audio.wav";
  const displayTitle = title || fileName;

  // Portal kullanarak modal'ı document.body'ye render et
  const modalContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[99999] bg-black/60 backdrop-blur-xl dark:bg-black/70 dark:backdrop-blur-xl"
        onClick={onClose}
        style={{
          pointerEvents: "auto",
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        <div
          className="fixed inset-0 flex items-center justify-center p-4 sm:p-6"
          onClick={(e) => {
            // Close when clicking outside content
            e.stopPropagation();
            if (e.target === e.currentTarget) {
              onClose();
            }
          }}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            transition={{ type: "spring", duration: 0.4, bounce: 0.2 }}
            className="relative max-w-full max-h-full w-full max-w-lg pointer-events-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <Button
              variant="secondary"
              size="icon"
              className="absolute top-4 right-4 z-50 rounded-full h-9 w-9 shadow-xl border border-white/20 dark:border-gray-700/50 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-700 transition-all"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
            >
              <X className="h-4 w-4 text-gray-700 dark:text-gray-200" />
            </Button>

            {/* Modern Audio Player Card */}
            <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-br from-white/95 via-white/90 to-white/95 dark:from-gray-900/95 dark:via-gray-900/90 dark:to-gray-900/95 backdrop-blur-2xl border border-white/20 dark:border-gray-700/50 shadow-2xl">
              {/* Decorative gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-pink-500/5 dark:from-blue-500/10 dark:via-purple-500/10 dark:to-pink-500/10 pointer-events-none" />

              <div className="relative p-6 sm:p-8 md:p-10">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="flex flex-col items-center gap-6"
                >
                  {/* Audio Icon with Animation */}
                  <motion.div
                    animate={{
                      scale: isPlaying ? [1, 1.1, 1] : 1,
                    }}
                    transition={{
                      duration: 2,
                      repeat: isPlaying ? Infinity : 0,
                      ease: "easeInOut",
                    }}
                    className="relative"
                  >
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 dark:from-blue-600 dark:to-purple-700 flex items-center justify-center shadow-lg">
                      <Music2 className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
                    </div>
                    {isPlaying && (
                      <motion.div
                        animate={{
                          scale: [1, 1.2, 1],
                          opacity: [0.5, 0.8, 0.5],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                        className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/30 to-purple-600/30"
                      />
                    )}
                  </motion.div>

                  {/* Title */}
                  <div className="text-center w-full">
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100 truncate px-4">
                      {displayTitle}
                    </h3>
                  </div>

                  {/* Hidden Audio Element */}
                  <audio ref={audioRef} src={audio} autoPlay className="hidden">
                    {t("common.audioNotSupported", {
                      defaultValue: "Tarayıcınız ses oynatmayı desteklemiyor.",
                    })}
                  </audio>

                  {/* Custom Audio Controls */}
                  <div className="w-full space-y-4">
                    {/* Progress Bar */}
                    <div className="relative">
                      <div
                        ref={progressBarRef}
                        onClick={handleProgressChange}
                        className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full cursor-pointer overflow-hidden"
                      >
                        <motion.div
                          className="absolute left-0 top-0 h-full bg-gradient-to-r from-blue-500 to-purple-600 dark:from-blue-600 dark:to-purple-700 rounded-full"
                          initial={{ width: 0 }}
                          animate={{
                            width:
                              duration > 0
                                ? `${(currentTime / duration) * 100}%`
                                : 0,
                          }}
                          transition={{ duration: 0.1 }}
                        />
                        <div
                          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white dark:bg-gray-200 rounded-full shadow-md border-2 border-blue-500 dark:border-blue-600"
                          style={{
                            left:
                              duration > 0
                                ? `calc(${
                                    (currentTime / duration) * 100
                                  }% - 8px)`
                                : 0,
                          }}
                        />
                      </div>
                    </div>

                    {/* Time Display and Controls */}
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 tabular-nums">
                        {formatTime(currentTime)}
                      </span>

                      {/* Play/Pause Button */}
                      <Button
                        onClick={handleTogglePlay}
                        size="lg"
                        className="rounded-full h-14 w-14 sm:h-16 sm:w-16 bg-gradient-to-br from-blue-500 to-purple-600 dark:from-blue-600 dark:to-purple-700 hover:from-blue-600 hover:to-purple-700 dark:hover:from-blue-700 dark:hover:to-purple-800 shadow-lg hover:shadow-xl transition-all"
                      >
                        {isPlaying ? (
                          <motion.div
                            key="pause"
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Pause className="w-6 h-6 sm:w-7 sm:h-7 text-white fill-white" />
                          </motion.div>
                        ) : (
                          <motion.div
                            key="play"
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Play className="w-6 h-6 sm:w-7 sm:h-7 text-white fill-white ml-0.5" />
                          </motion.div>
                        )}
                      </Button>

                      <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 tabular-nums">
                        {formatTime(duration)}
                      </span>
                    </div>
                  </div>

                  {/* Download Button */}
                  {!miniAppBridgeService.isInMiniApp() && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <Button
                        variant="outline"
                        size="sm"
                        className="group relative gap-2 px-4 py-2.5 rounded-xl border border-white/30 dark:border-gray-600/50 bg-gradient-to-br from-white/80 via-white/70 to-white/60 dark:from-gray-800/80 dark:via-gray-800/70 dark:to-gray-800/60 backdrop-blur-md hover:from-white dark:hover:from-gray-700 hover:via-white dark:hover:via-gray-700 hover:to-white dark:hover:to-gray-700 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload();
                        }}
                      >
                        {/* Shine effect on hover */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />

                        <Download className="h-4 w-4 text-gray-700 dark:text-gray-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors relative z-10" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors relative z-10">
                          {t("wizard.download") ||
                            t("common.download", { defaultValue: "İndir" })}
                        </span>
                      </Button>
                    </motion.div>
                  )}
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );

  // Portal kullanarak document.body'ye render et
  return createPortal(modalContent, document.body);
}
